import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import styled from 'styled-components';
import { Utensils } from 'lucide-react';
import { Container, Button, Card, Grid } from '../../styles/GlobalStyles';

// ========== ANTI-FLICKER TECHNIQUES ========== //

// 1. STABLE REFERENCES - Move outside component to prevent re-creation
const STABLE_BUSINESS_HOURS = {
  0: { day: 'Sunday', open: 12, close: 18 },
  1: { day: 'Monday', open: 11, close: 22 },
  2: { day: 'Tuesday', open: 11, close: 22 },
  3: { day: 'Wednesday', open: 11, close: 22 },
  4: { day: 'Thursday', open: 11, close: 22 },
  5: { day: 'Friday', open: 11, close: 23 },
  6: { day: 'Saturday', open: 12, close: 23 }
} as const;

// 2. STABLE FUNCTIONS - Outside component to prevent re-creation
const formatTime = (hour: number): string => {
  if (hour === 0) return '12 AM';
  if (hour < 12) return `${hour} AM`;
  if (hour === 12) return '12 PM';
  return `${hour - 12} PM`;
};

const HeroSection = styled.section`
  min-height: 100vh;
  position: relative;
  display: flex;
  align-items: center;
  padding: 6rem 0 4rem;
  overflow: hidden;
  
  /* Video background container */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, 
      rgba(0, 0, 0, 0.7) 0%, 
      rgba(139, 0, 0, 0.3) 50%, 
      rgba(0, 0, 0, 0.8) 100%
    );
    z-index: 2;
  }
  
  /* Animated background pattern */
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: 
      radial-gradient(circle at 25% 25%, rgba(139, 0, 0, 0.1) 0%, transparent 50%),
      radial-gradient(circle at 75% 75%, rgba(255, 215, 0, 0.1) 0%, transparent 50%);
    animation: backgroundFloat 20s ease-in-out infinite;
    z-index: 1;
  }
  
  @keyframes backgroundFloat {
    0%, 100% { transform: translateY(0px) rotate(0deg); }
    50% { transform: translateY(-20px) rotate(1deg); }
  }
`;

const VideoBackground = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  min-width: 100%;
  min-height: 100%;
  width: auto;
  height: auto;
  transform: translateX(-50%) translateY(-50%);
  z-index: 0;
  filter: brightness(0.4) contrast(1.2) saturate(1.1);
  
  iframe {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const HeroContent = styled.div`
  text-align: center;
  margin-bottom: 4rem;
  position: relative;
  z-index: 3;
`;

const EmojiTitle = styled.div`
  font-size: 4rem;
  margin-bottom: 1rem;
  
  @media (max-width: 768px) {
    font-size: 3rem;
  }
`;

const MainTitle = styled.h1`
  font-size: 3.5rem;
  font-weight: 800;
  margin-bottom: 1rem;
  line-height: 1.1;
  
  .royale {
    background: ${props => props.theme.colors.gradients.secondary};
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
  
  .with-cheese {
    background: ${props => props.theme.colors.gradients.primary};
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
  
  @media (max-width: 480px) {
    font-size: 2rem;
  }
`;

const Subtitle = styled.p`
  font-size: 1.125rem;
  color: ${props => props.theme.colors.text.secondary};
  max-width: 600px;
  margin: 0 auto 2rem;
  line-height: 1.6;
  font-style: italic;
  
  @media (max-width: 768px) {
    font-size: 1rem;
    padding: 0 1rem;
  }
`;

const ActionGrid = styled(Grid)`
  margin-top: 3rem;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 1rem;
  }
`;

const ActionCard = styled(Card).withConfig({
  shouldForwardProp: (prop) => !['bgGradient', 'borderColor'].includes(prop)
})<{ 
  bgGradient: string;
  borderColor?: string;
}>`
  text-align: center;
  background: ${props => props.bgGradient};
  border: 2px solid ${props => props.borderColor || 'transparent'};
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.2);
    z-index: 1;
  }
  
  > * {
    position: relative;
    z-index: 2;
  }
`;

const CardStatus = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
  font-size: 0.875rem;
  font-weight: 600;
`;

const StatusIndicator = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'color'
})<{ color: string }>`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: ${props => props.color};
  box-shadow: 0 0 10px ${props => props.color};
`;

const CardTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
  color: white;
`;

const CardDescription = styled.p`
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 1.5rem;
`;

// Glass Effect Button Component - Using working test button pattern
const GlassButton: React.FC<{
  onClick?: () => void;
  children: React.ReactNode;
  disabled?: boolean;
  style?: React.CSSProperties;
}> = ({ onClick, children, disabled, style }) => {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        width: '100%',
        padding: '0.75rem 1.5rem',
        background: 'rgba(255, 255, 255, 0.15)', // More transparent for video visibility
        backdropFilter: 'blur(3px)', // Subtle blur to see video through
        border: '1px solid rgba(255, 255, 255, 0.3)',
        borderRadius: '12px',
        color: 'white',
        fontWeight: '600',
        fontSize: '0.875rem',
        cursor: disabled ? 'not-allowed' : 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '0.5rem',
        position: 'relative',
        zIndex: 100,
        outline: 'none',
        opacity: disabled ? 0.6 : 1,
        
        // Touch optimization
        WebkitTapHighlightColor: 'transparent',
        WebkitTouchCallout: 'none',
        WebkitUserSelect: 'none',
        userSelect: 'none',
        touchAction: 'manipulation',
        
        // Simple transitions
        transition: 'background-color 0.1s ease, border-color 0.1s ease, backdrop-filter 0.1s ease',
        
        ...style
      }}
      onMouseEnter={(e) => {
        if (!disabled && window.innerWidth >= 1024) {
          e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.25)';
          e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.5)';
          e.currentTarget.style.backdropFilter = 'blur(5px)';
        }
      }}
      onMouseLeave={(e) => {
        if (!disabled) {
          e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.15)';
          e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.3)';
          e.currentTarget.style.backdropFilter = 'blur(3px)';
        }
      }}
      onTouchStart={(e) => {
        if (!disabled) {
          e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.3)';
          e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.6)';
        }
      }}
      onTouchEnd={(e) => {
        if (!disabled) {
          e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.15)';
          e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.3)';
        }
      }}
    >
      {children}
    </button>
  );
};

const BottomActions = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  margin-top: 3rem;
  flex-wrap: wrap;
`;

const MainCTA = styled(Button)`
  font-size: 1.125rem;
  padding: 1rem 2rem;
  
  @media (max-width: 768px) {
    width: 100%;
    margin: 0 1rem;
  }
`;

const SecondaryButton = styled(Button)`
  @media (max-width: 768px) {
    width: 100%;
    margin: 0 1rem;
  }
`;

const FloatingMenuPreview = styled.div`
  position: absolute;
  top: 20%;
  right: 5%;
  width: 280px;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-radius: 20px;
  padding: 1.5rem;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  z-index: 10;
  animation: float 6s ease-in-out infinite;
  
  @keyframes float {
    0%, 100% { transform: translateY(0px); }
    50% { transform: translateY(-10px); }
  }
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const PreviewTitle = styled.h4`
  font-size: 0.875rem;
  font-weight: 600;
  color: #4a5568;
  margin-bottom: 1rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const PreviewItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
  padding: 0.5rem;
  border-radius: 8px;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(220, 38, 38, 0.05);
  }
`;

const PreviewImage = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 8px;
      background: linear-gradient(135deg, #FFD700, #FFC300);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
`;

const PreviewInfo = styled.div`
  flex: 1;
`;

const PreviewName = styled.div`
  font-size: 0.875rem;
  font-weight: 600;
  color: #1a1a1a;
`;

const PreviewPrice = styled.div`
  font-size: 0.75rem;
  color: #DC2626;
  font-weight: 600;
`;

const StatsBar = styled.div`
  display: flex;
  justify-content: center;
  gap: 3rem;
  margin-top: 2rem;
  padding: 1.5rem;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
    text-align: center;
  }
`;

const StatItem = styled.div`
  text-align: center;
`;

const StatNumber = styled.div`
  font-size: 2.5rem;
  font-weight: 900;
  color: #FFFFFF;
  text-shadow: 0 0 20px rgba(255, 255, 255, 0.8), 0 0 40px rgba(255, 255, 255, 0.4);
  margin-bottom: 0.25rem;
  background: linear-gradient(135deg, #FFFFFF 0%, #F8FAFC 50%, #FFFFFF 100%);
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  filter: brightness(1.2) contrast(1.1);
  letter-spacing: -0.5px;
`;

const StatLabel = styled.div`
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.95);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 600;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
`;



const Hero: React.FC = () => {
  // 3. STABLE STATE MANAGEMENT
  const [currentTime, setCurrentTime] = useState(() => new Date());
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  // 4. ULTRA-STABLE TIMER - Only update when hour actually changes
  const updateTime = useCallback(() => {
    setCurrentTime(new Date());
  }, []);

  // 5. HOUR-BASED TIMER - Only update when hour changes to eliminate flickering
  useEffect(() => {
    updateTime(); // Initial update
    
    const scheduleNextHourUpdate = () => {
      const now = new Date();
      const nextHour = new Date(now);
      nextHour.setHours(now.getHours() + 1, 0, 0, 0); // Next hour at :00 minutes
      const msUntilNextHour = nextHour.getTime() - now.getTime();
      
      timerRef.current = setTimeout(() => {
        updateTime();
        scheduleNextHourUpdate(); // Schedule next hour update
      }, msUntilNextHour);
    };

    scheduleNextHourUpdate();
    
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [updateTime]);

  // Extract stable values for memoization - only update when text should actually change
  const dayOfWeek = currentTime.getDay();
  const currentHour = currentTime.getHours();

  // Simple working business status logic (same as ButtonTest)
  const businessStatus = useMemo(() => {
    const todayHours = STABLE_BUSINESS_HOURS[dayOfWeek as keyof typeof STABLE_BUSINESS_HOURS];
    const isOpen = currentHour >= todayHours.open && currentHour < todayHours.close;
    
    let statusMessage: string = '';
    let nextStateChange: string = '';
    
    if (!isOpen && currentHour < todayHours.open) {
      // Before opening today
      nextStateChange = `Opens at ${formatTime(todayHours.open)}`;
      statusMessage = `We open at ${formatTime(todayHours.open)}`;
    } else if (isOpen) {
      // Currently open
      nextStateChange = `Closes at ${formatTime(todayHours.close)}`;
      statusMessage = `Fresh, hot & ready for pickup`;
    } else {
      // After closing, find next opening day
      let nextDay = (dayOfWeek + 1) % 7;
      let daysUntilOpen = 1;
      
      // Find the next day we're open
      while (daysUntilOpen < 7) {
        const nextDayHours = STABLE_BUSINESS_HOURS[nextDay as keyof typeof STABLE_BUSINESS_HOURS];
        // Check if the restaurant is open that day (has different open/close times)
        const dayName = nextDayHours.day;
        nextStateChange = `Opens ${dayName} at ${formatTime(nextDayHours.open)}`;
        statusMessage = `Opens ${dayName} at ${formatTime(nextDayHours.open)}`;
        break;
      }
      
      // Fallback if no logic matched
      if (!statusMessage) {
        nextStateChange = `Closed for today`;
        statusMessage = `See you tomorrow!`;
      }
    }
    
    return { isOpen, statusMessage, nextStateChange };
  }, [dayOfWeek, currentHour]);

  // 6. STABLE EVENT HANDLERS - useCallback prevents re-creation
  const scrollToSection = useCallback((sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  }, []);

  const handleGetDirections = useCallback(() => {
    const address = "4163 Cass Ave, Detroit, MI 48201";
    const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
    window.open(googleMapsUrl, '_blank');
  }, []);

  const handleCallStore = useCallback(() => {
    window.location.href = 'tel:+13133153014';
  }, []);

  const handleSignUp = useCallback(() => {
    // In production, this would integrate with your actual signup system
    // For now, redirect to a contact form or phone number
    window.location.href = 'tel:+13133153014';
  }, []);

  const handleSpecialOrder = useCallback(() => {
    // In production, this would integrate with your ordering system
    // For now, scroll to menu section
    scrollToSection('menu');
  }, [scrollToSection]);

  // Use simple Utensils icon for special orders
  const SpecialIcon = Utensils;

  return (
    <HeroSection id="hero">
      {/* Cloudflare Stream Video Background */}
      <VideoBackground>
        <div style={{ position: 'relative', paddingTop: '177.77777777777777%' }}>
          <iframe
            src="https://customer-6njalxhlz5ulnoaq.cloudflarestream.com/7ae53ba1efd845285e30a51dce7620af/iframe?autoplay=true&muted=true&loop=true&controls=false&poster=https%3A%2F%2Fcustomer-6njalxhlz5ulnoaq.cloudflarestream.com%2F7ae53ba1efd845285e30a51dce7620af%2Fthumbnails%2Fthumbnail.jpg%3Ftime%3D%26height%3D600"
            loading="lazy"
            style={{ 
              border: 'none', 
              position: 'absolute', 
              top: 0, 
              left: 0, 
              height: '100%', 
              width: '100%',
              pointerEvents: 'none'
            }}
            allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
            allowFullScreen={true}
            title="Royale with Cheese Brand Video"
          />
        </div>
      </VideoBackground>

      <Container>
        <HeroContent>
          <EmojiTitle>🍔👑</EmojiTitle>
          <MainTitle>
            <span className="royale">ROYALE</span><br />
            <span className="with-cheese">WITH CHEESE</span>
          </MainTitle>
          <Subtitle>
            Detroit's most awarded burger destination featuring AI-powered ordering,
            4.9-star rated experience, and the city's most profitable restaurant technology
            showcase
          </Subtitle>
        </HeroContent>



        <BottomActions>
          <MainCTA 
            variant="primary" 
            size="large"
            onClick={businessStatus.isOpen ? () => scrollToSection('menu') : undefined}
            style={{ 
              opacity: businessStatus.isOpen ? 1 : 0.7,
              cursor: businessStatus.isOpen ? 'pointer' : 'not-allowed'
            }}
          >
            <StatusIndicator color={businessStatus.isOpen ? "#10B981" : "#EF4444"} />
            {businessStatus.isOpen ? "Order Now - We're Open!" : businessStatus.nextStateChange}
          </MainCTA>
          <SecondaryButton 
            variant="outline" 
            size="large"
            onClick={() => scrollToSection('menu')}
          >
            View Full Menu
          </SecondaryButton>
        </BottomActions>

        <StatsBar className="glass-white">
          <StatItem>
            <StatNumber>4.9</StatNumber>
            <StatLabel>Rating</StatLabel>
          </StatItem>
          <StatItem>
            <StatNumber>3K+</StatNumber>
<StatLabel>Reviews</StatLabel>
          </StatItem>
          <StatItem>
            <StatNumber>15min</StatNumber>
            <StatLabel>Avg Prep</StatLabel>
          </StatItem>
          <StatItem>
            <StatNumber>24/7</StatNumber>
            <StatLabel>AI Support</StatLabel>
          </StatItem>
        </StatsBar>
      </Container>


    </HeroSection>
  );
};

export default Hero; 